/** biome-ignore-all lint/a11y/useMediaCaption: third-party ad creative, no track available */
"use client";

/**
 * NewForm design-system VideoPlayer.
 *
 * A video in a frame that matches the file's own shape. The aspect ratio is
 * measured from the media once its metadata loads, so a 9:16 TikTok, a 16:9
 * spot, and a 1:1 square each get an exact container instead of being cropped
 * into a house shape — which is the whole point when the thing being reviewed
 * IS the framing.
 *
 * Controls follow <AdMediaPreview>, the player the creative-testing ad modal
 * uses — click the frame to play or pause, mute in the corner, a badge and a
 * centred play button, and `showScrubber` for a seek bar and time readout.
 * Native `controls` were a browser chrome bar pinned across the bottom of the
 * creative, in a different visual language on every platform.
 *
 * What this does differently to <AdMediaPreview> is the frame: that one crops
 * to a fixed 9:16 with `object-cover`, which is right for a uniform grid of
 * house creative and wrong when the file's own proportions are the thing
 * being reviewed.
 *
 *   <VideoPlayer
 *     src={ad.videoUrl}
 *     poster={ad.cover}
 *     aspectRatio="9:16"          // optional — a hint until metadata lands
 *     caption="She was SHOOK…"    // optional overlay across the bottom
 *     durationLabel="0:34"        // optional chip, top-right
 *     onAspectRatio={setRatio}    // optional — parent can lay out around it
 *   />
 *
 * See ../../../../packages/nf-design-system/preview for the surrounding spec.
 */

import {
  Maximize2,
  Minimize2,
  Pause,
  Play,
  Volume2,
  VolumeX,
} from "lucide-react";
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { cn } from "@/lib/utils";
import { VideoScrubber } from "./video-scrubber";

/** The shapes worth naming. Anything else is passed as `width / height`. */
export type NamedAspectRatio = "9:16" | "16:9" | "1:1" | "4:5" | "5:4";

const NAMED_RATIOS: Record<NamedAspectRatio, number> = {
  "9:16": 9 / 16,
  "16:9": 16 / 9,
  "1:1": 1,
  "4:5": 4 / 5,
  "5:4": 5 / 4,
};

/**
 * Whether filling a frame pinned at 9:16 would crop next to nothing: under 2%
 * of the file's width or height. True for a 9:16 export a few pixels off the
 * ratio; false for a 3:5, 4:5 or square file, whose sides a fill would cut,
 * captions included. Deliberately tighter than the tolerance
 * `describeAspectRatio` names a shape by, which answers a different question.
 */
export function fillsNineBySixteen(ratio: number): boolean {
  return Math.abs(ratio / (9 / 16) - 1) < 0.02;
}

/** `9:16 vertical`, `16:9 landscape` — how the shape is described in copy. */
export function describeAspectRatio(ratio: number): string {
  if (Math.abs(ratio - 1) < 0.02) return "1:1 square";
  if (ratio < 1) {
    return Math.abs(ratio - 9 / 16) < 0.06 ? "9:16 vertical" : "vertical";
  }
  return Math.abs(ratio - 16 / 9) < 0.12 ? "16:9 landscape" : "landscape";
}

export function parseAspectRatio(
  value: NamedAspectRatio | number | null | undefined,
): number | null {
  if (typeof value === "number") return value > 0 ? value : null;
  if (!value) return null;
  return NAMED_RATIOS[value] ?? null;
}

export interface VideoPlayerProps {
  src?: string | null;
  poster?: string | null;
  /** Known shape, if the source reports one. Superseded once metadata loads. */
  aspectRatio?: NamedAspectRatio | number | null;
  caption?: string | null;
  durationLabel?: string | null;
  className?: string;
  /** Fires with `width / height` once the real dimensions are known. */
  onAspectRatio?: (
    ratio: number,
    size: { width: number; height: number },
  ) => void;
  /**
   * Keep the frame at `aspectRatio` even once the file reports something
   * different — for a carousel, where every slide re-measuring means the box
   * resizes under the arrows. The media letterboxes instead, which is what
   * `object-contain` is already for. The measurement is still reported up.
   */
  lockAspectRatio?: boolean;
  /**
   * A seek bar and a `0:04 / 0:16` readout across the foot of the frame.
   *
   * Opt-in and off by default, with the same name and the same default as
   * `<AdMediaPreview showScrubber>` — the two players sit beside each other
   * in the product and a viewer should not have to learn that one of them
   * spells it differently. Like that one it replaces the caption, which
   * occupies the same edge.
   *
   * Right for a player someone opened to watch; wrong for a grid of
   * thumbnails, where a control bar on every tile is chrome competing with
   * the creative it sits on.
   */
  showScrubber?: boolean;
  /**
   * How the media sits in the frame. `contain` fits the whole file and
   * letterboxes whatever the frame has spare; `cover` fills the frame and
   * crops the overflow.
   *
   * `contain` is the default because this player exists to show a file at
   * its own proportions. `cover` is for a caller that has pinned the frame
   * with `lockAspectRatio` and wants it filled — an ad library of nominally
   * 9:16 creative, where the files are a few pixels off the ratio and the
   * bars that buys are more distracting than the sliver they cost.
   */
  fit?: "contain" | "cover";
  /**
   * Start playing as soon as the file can, muted: browsers allow autoplay
   * only without sound, and the player starts muted anyway. For a video that
   * is the point of the surface it sits on (a lone example beside a brief),
   * not for a grid, where several videos starting at once is noise.
   */
  autoPlay?: boolean;
  /**
   * A full-screen button beside mute. The whole frame goes full screen, so
   * the player's own controls come with it. Off by default: right for a
   * player someone opened to watch closely, noise on a grid of thumbnails.
   */
  allowFullscreen?: boolean;
}

// iOS Safari has no element full screen, only the video's own player.
type IosVideo = HTMLVideoElement & { webkitEnterFullscreen?: () => void };

export function VideoPlayer({
  src,
  poster,
  aspectRatio,
  caption,
  durationLabel,
  className,
  onAspectRatio,
  lockAspectRatio = false,
  showScrubber = false,
  fit = "contain",
  autoPlay = false,
  allowFullscreen = false,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (!allowFullscreen) return;
    const sync = () =>
      setFullscreen(
        Boolean(frameRef.current) &&
          document.fullscreenElement === frameRef.current,
      );
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, [allowFullscreen]);

  const toggleFullscreen = useCallback(() => {
    const frame = frameRef.current;
    if (!frame) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else if (frame.requestFullscreen) {
      void frame.requestFullscreen();
    } else {
      (videoRef.current as IosVideo | null)?.webkitEnterFullscreen?.();
    }
  }, []);
  const [measured, setMeasured] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  // Falls back to 9:16: every source this is pointed at so far is social ad
  // creative, and guessing wrong is one reflow rather than a wrong crop.
  const declared = parseAspectRatio(aspectRatio);
  const ratio = lockAspectRatio
    ? (declared ?? measured ?? 9 / 16)
    : (measured ?? declared ?? 9 / 16);

  const handleMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video.videoHeight) return;
    const next = video.videoWidth / video.videoHeight;
    setMeasured(next);
    onAspectRatio?.(next, {
      width: video.videoWidth,
      height: video.videoHeight,
    });
  }, [onAspectRatio]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  }, []);

  const frameStyle: CSSProperties = { aspectRatio: String(ratio) };

  if (!src) {
    return (
      <div
        style={frameStyle}
        className={cn(
          "flex w-full items-center justify-center overflow-hidden rounded-nf-ds-xl bg-nf-ds-bg-surface-2",
          className,
        )}
      >
        <span className="font-nf-ds-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground">
          No preview
        </span>
      </div>
    );
  }

  return (
    <div
      ref={frameRef}
      style={frameStyle}
      className={cn(
        "relative w-full overflow-hidden rounded-nf-ds-xl",
        // The backdrop is the letterbox, so it belongs to `contain` only.
        // Under `cover` the media fills the frame by construction and the
        // fill has nothing left to do — except show as a dark seam along an
        // edge wherever the browser rounds the raster against the box, which
        // reads as a black border on a light creative.
        fit === "contain" && "bg-nf-ds-ink",
        className,
      )}
    >
      {/* The frame is the play/pause target, as in AdMediaPreview — a video
          this size is easier to hit than any button on it. */}
      <button
        type="button"
        onClick={togglePlay}
        aria-label={playing ? "Pause video" : "Play video"}
        className="absolute inset-0 cursor-pointer"
      >
        <video
          ref={videoRef}
          src={src}
          poster={poster ?? undefined}
          preload="metadata"
          playsInline
          loop
          autoPlay={autoPlay}
          muted={muted}
          onLoadedMetadata={handleMetadata}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          className={cn(
            "size-full",
            fit === "cover" ? "object-cover" : "object-contain",
          )}
        />
      </button>

      {durationLabel && (
        <span className="pointer-events-none absolute left-nf-ds-2 top-nf-ds-2 inline-flex h-[22px] items-center gap-1 rounded-nf-ds-sm bg-nf-ds-ink/65 px-2 font-nf-ds-mono text-[10px] uppercase leading-none tracking-[0.08em] text-nf-ds-white backdrop-blur-sm">
          <Play className="size-2.5 fill-current" />
          {durationLabel}
        </span>
      )}

      <button
        type="button"
        onClick={() => setMuted((current) => !current)}
        aria-label={muted ? "Unmute" : "Mute"}
        className="absolute right-nf-ds-2 top-nf-ds-2 inline-flex size-7 cursor-pointer items-center justify-center rounded-nf-ds-pill bg-nf-ds-ink/65 text-nf-ds-white backdrop-blur-sm transition-colors hover:bg-nf-ds-ink/80"
      >
        {muted ? (
          <VolumeX className="size-3.5" />
        ) : (
          <Volume2 className="size-3.5" />
        )}
      </button>

      {allowFullscreen ? (
        <button
          type="button"
          onClick={toggleFullscreen}
          aria-label={fullscreen ? "Exit full screen" : "Full screen"}
          className="absolute right-nf-ds-10 top-nf-ds-2 inline-flex size-7 cursor-pointer items-center justify-center rounded-nf-ds-pill bg-nf-ds-ink/65 text-nf-ds-white backdrop-blur-sm transition-colors hover:bg-nf-ds-ink/80"
        >
          {fullscreen ? (
            <Minimize2 className="size-3.5" />
          ) : (
            <Maximize2 className="size-3.5" />
          )}
        </button>
      ) : null}

      {/* Centred affordance while paused, and a soft hint on hover while
          playing — so the frame reads as clickable in both states. */}
      <span
        className={cn(
          "pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity",
          playing ? "opacity-0 hover:opacity-100" : "opacity-100",
        )}
      >
        <span className="flex size-12 items-center justify-center rounded-nf-ds-pill bg-nf-ds-ink/45 text-nf-ds-white backdrop-blur-sm">
          {playing ? (
            <Pause className="size-5 fill-current" />
          ) : (
            <Play className="size-5 translate-x-px fill-current" />
          )}
        </span>
      </span>

      {showScrubber && src ? (
        <VideoScrubber
          videoRef={videoRef}
          mediaKey={src}
          className="absolute inset-x-0 bottom-0"
        />
      ) : null}

      {caption && !playing && !showScrubber && (
        <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-nf-ds-ink/85 to-transparent px-nf-ds-3 pb-nf-ds-3 pt-nf-ds-8 font-nf-ds-sans text-[13px] font-[600] leading-[1.35] text-nf-ds-white">
          {caption}
        </span>
      )}
    </div>
  );
}
